import { useQuery } from '@tanstack/react-query';
import { ConfiguratorApi } from '@/utils/ConfiguratorApi';

export type UseProdSyncedPhraseKeysResult = {
  syncedKeys: Set<string>;
  isLoading: boolean;
  error: Error | null;
  isSuccess: boolean;
};

/**
 * Fetches the list of phrase keys that are already synced to Phrase Prod.
 * These keys should be read-only in the configurator to prevent accidental
 * deletion of SEO metadata and refined translations.
 *
 * @param identifier - The planner identifier (e.g., 'pax-planner')
 * @param enabled - Whether to enable the query (disable for floor-calculator)
 */
export function useProdSyncedPhraseKeys(
  identifier: string | undefined,
  enabled: boolean = true,
): UseProdSyncedPhraseKeysResult {
  const { data, isLoading, error, isSuccess } = useQuery<
    Record<string, string>,
    Error
  >({
    queryKey: ['prod-synced-phrase-keys', identifier],
    queryFn: async () => {
      if (!identifier) {
        return {};
      }
      return ConfiguratorApi.getInstance().getTranslation(
        identifier,
        'production',
        'en',
        'xz',
      );
    },
    enabled: enabled && !!identifier,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const syncedKeys = new Set(Object.keys(data ?? {}));

  return {
    syncedKeys,
    isLoading,
    error: error ?? null,
    isSuccess,
  };
}
